/**
 * Copyright 2019 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { useState, useEffect, useMemo } from 'react';
import { Provider } from 'react-redux';
import App, { type AppProps } from 'next/app';
import Head from 'next/head';
import {
  QueryClientProvider,
  HydrationBoundary,
  type DehydratedState,
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import AppComp from '@/components/App';
import ErrorBoundaryComp from '@/components/ErrorBoundary/ErrorBoundary';
import { ThemeContext, type ThemeDeviceType, createTheme } from '@/theme/ThemeContext';
// this needs to be imported after stylesheets to ensure correct css order
import { getOrCreateStore } from '../configureStore';
import { getAxios, getAxiosForClient } from '@/lib/axios';
import { configureAxiosForClientWithStore } from '@/lib/axios/configureAxiosForClient';
// This is needed to workaround an issue related to dynamic imports in Next.js
// For reference, see https://track.autozone.com/browse/SB-1261 and https://github.com/vercel/next.js/issues/17464
import '../components/AZCustomComponent/FormGroup/TextField/formikTextField';
import { getClientSideQueryClient, createQueryClient } from '@/lib/react-query';
import { AppStateProvider } from '@/stores/AppState';
import type { AppState } from '@/stores/interface';
import dynamic from 'next/dynamic';
import { getDeviceType } from '@/utils/getDeviceType.server';
import {
  NotificationProvider,
  type NotificationProviderProps,
  Starc,
  useIsomorphicLayoutEffect,
  OneTrustProvider,
} from '@az/starc-ui';
import { SnackBarProvider } from '@/components/SnackBarContext';
import { SignInFlowTypeProvider } from '@/components/SignInFlowTypeContext';
import { Banner90DayProvider } from '@/components/Banner90DayContext';
import { RetryConfigProvider } from '@/components/RetryConfigContext';
import { SearchBoxProvider } from '@/components/SearchBoxContext';
import { ModalTCPromptProvider } from '@/components/ModalTCPromptContext';
import { handleCitrusForceProdPassthrough } from '@/features/citrus/lib/handleCitrusForceProdPassthrough.server'; // eslint-disable-line no-restricted-imports
import { setCookie } from '@/utils/cookie';
import { getGlobalConfigFromCache } from '@/features/globalConfig';
import { isServer } from '@/utils/isServer';
import { StarcLinkOverride } from '@/components/StarcLinkOverride/StarcLinkOverride';
import { useRouter as useAppRouter } from 'next/navigation';
import { useDispatchNavigationEvents } from '@/hooks/useDispatchNavigationEvents';
import { useLegacyRouter } from '@/hooks/useLegacyRouter';
import environmentConfig from '@/config/environment';
import { reportMissingLabels } from '@/features/performance/utils/reportMissingLabels';
import { type NEXT_DATA } from 'next/dist/shared/lib/utils';
import { ModalOPPromptProvider } from '@/components/ModalOPPromptContext';
import '../theme/normalize.css';
import '../theme/configTechOverrides.css';
import '../theme/loqateOverrides.css';
import '@az/starc-ui/themes/starc.css';
import '../theme/globals.scss';

if (process.env.NEXT_PUBLIC_API_MOCKING === 'true') {
  /* eslint-disable-next-line @typescript-eslint/no-require-imports */
  require('../mocks');
}

const reuseDataFetchedInBrowser: () => NEXT_DATA['props'] = () => {
  const initialDataElement = document.querySelector('#__NEXT_DATA__');
  return initialDataElement !== null
    ? (JSON.parse(initialDataElement.innerHTML) as NEXT_DATA).props
    : {};
};

const getInitialData = (isServer: boolean) => {
  if (!isServer) {
    return reuseDataFetchedInBrowser();
  }
};

type PageProps = {
  initialAppState: AppState;
  dehydratedState: DehydratedState;
};

type Props = AppProps<PageProps> & {
  Component: React.ComponentType<PageProps> & {
    getLayout?: (page: React.ReactElement, pageProps?: object) => React.ReactNode;
  };
  responsiveContext: {
    width: number;
  };
  deviceType: ThemeDeviceType;
};

const ReactQueryDevtoolsProduction = dynamic(() =>
  import('@tanstack/react-query-devtools/build/modern/production.js').then(
    (m) => m.ReactQueryDevtools,
  ),
);

const AppDevtools = dynamic(() =>
  import('@/features/common/components/AppDevtools').then((m) => m.AppDevtools),
);

const LazyINPMeasurements = dynamic(
  () =>
    import('@/features/performance/components/INPMeasurements').then((mod) => mod.INPMeasurements),
  { ssr: false },
);

const starcOverrides = {
  link: StarcLinkOverride,
};

const notificationProviderOptions: NotificationProviderProps['options'] = {
  zIndex: 999,
};

function CustomApp(props: Props) {
  const { Component, ...rest } = props;
  const { pageProps, deviceType } = rest as Props;

  const isBot = deviceType === 'BOT';

  const [queryClient] = useState(() =>
    isServer ? createQueryClient({ isBot }) : getClientSideQueryClient({ isBot }),
  );
  const [store] = useState(() => {
    return getOrCreateStore(getAxios(undefined));
  });
  const [showDevtools, setShowDevtools] = useState(false);
  const [shouldLogINP, setShouldLogINP] = useState(false);

  useDispatchNavigationEvents();
  useEffect(() => {
    window.toggleDevtools = () => setShowDevtools((old) => !old);
  }, []);

  useIsomorphicLayoutEffect(() => {
    const MILISECONDS_IN_MONTH = 2629746000;
    void setCookie('eCookieId', pageProps.initialAppState.eCookieId, {
      secure: true,
      expires: new Date(Date.now() + MILISECONDS_IN_MONTH * 13),
      sameSite: 'lax',
      path: '/',
    });
  }, [pageProps.initialAppState.eCookieId]);

  const getLayout = Component.getLayout || ((page) => page as React.ReactNode);

  const legacyRouter = useLegacyRouter();
  const appRouter = useAppRouter();

  useIsomorphicLayoutEffect(() => {
    configureAxiosForClientWithStore(
      getAxiosForClient(),
      store,
      queryClient,
      legacyRouter,
      appRouter,
    );
    // eslint-disable-next-line
  }, []);

  /**
   * There is a known issue in Nextjs (CSS Module resolution conflict with dynamic import in production mode)
   * Opened Issue in Github (https://github.com/vercel/next.js/issues/40080) - We must be aware of this issue
   * and make sure it is resolved before adding lazy loading to any styled components
   * We have begun work toward this in https://gitlab.autozone.com/digital-platform-retail/user-experience/web/web-ui/-/merge_requests/11458
   */

  const theme = useMemo(() => createTheme(deviceType), [deviceType]);

  useEffect(() => {
    const globalConfig = getGlobalConfigFromCache(queryClient);
    setShouldLogINP(globalConfig?.PERFORMANCE_INP_MEASUREMENTS_ENABLED === 'true');

    if (globalConfig?.LABEL_LOGGING_ENABLED === 'true') {
      window.addEventListener('visibilitychange', reportMissingLabels);
    }

    return () => {
      window.removeEventListener('visibilitychange', reportMissingLabels);
    };
  }, [queryClient]);

  return (
    <QueryClientProvider client={queryClient}>
      <Head>
        {/** Removing the shrink-to-fit and maximum-scale properties cause a zoom issue with text inputs with a font size less than 16px,
         * so unless those are changed throughout the site, please refrain from removing this (see comments in https://jira.autozone.com/browse/SB-3917) */}
        <meta
          key="metaViewport"
          name="viewport"
          content="width=device-width, initial-scale=1, shrink-to-fit=no, maximum-scale=1.0"
        />
      </Head>
      <HydrationBoundary state={pageProps.dehydratedState}>
        <Provider store={store}>
          <AppStateProvider initialState={pageProps.initialAppState}>
            <ThemeContext value={theme}>
              <Starc overrides={starcOverrides}>
                <NotificationProvider options={notificationProviderOptions}>
                  <SignInFlowTypeProvider>
                    <RetryConfigProvider>
                      <SnackBarProvider>
                        <ModalOPPromptProvider>
                          <ModalTCPromptProvider>
                            <Banner90DayProvider>
                              <SearchBoxProvider>
                                <OneTrustProvider>
                                  <ErrorBoundaryComp>
                                    {getLayout(
                                      <AppComp>
                                        <Component {...pageProps} />
                                      </AppComp>,
                                      pageProps,
                                    )}
                                  </ErrorBoundaryComp>
                                </OneTrustProvider>
                              </SearchBoxProvider>
                            </Banner90DayProvider>
                          </ModalTCPromptProvider>
                        </ModalOPPromptProvider>
                      </SnackBarProvider>
                    </RetryConfigProvider>
                  </SignInFlowTypeProvider>
                </NotificationProvider>
              </Starc>
              {shouldLogINP && <LazyINPMeasurements />}
            </ThemeContext>
            {environmentConfig.NODE_ENV === 'development' && <AppDevtools />}
          </AppStateProvider>
        </Provider>
      </HydrationBoundary>
      <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-right" />
      {showDevtools ? (
        <>
          <ReactQueryDevtoolsProduction buttonPosition="bottom-right" />
          <AppDevtools />
        </>
      ) : null}
    </QueryClientProvider>
  );
}

/**
 * Since next.js 9.3 getInitialProps is not called on the initial page load on the client.
 * It is called on client only in case of client-side routing.
 * While routing, we don't want to get all page initial data, we only want to get page-specific data.
 * */
CustomApp.getInitialProps = async (appContext: Parameters<typeof App.getInitialProps>[0]) => {
  const { ctx } = appContext;
  const { req, pathname } = ctx;

  const initialData = getInitialData(isServer);

  // we call Next's internal `getInitialProps` (to get props for the current page) inside the if statement
  // because it needs to be called after fetchInitialData

  if (isServer) {
    await handleCitrusForceProdPassthrough(ctx);
    const appProps = (await App.getInitialProps(appContext)) as Props;
    const deviceType = getDeviceType(req).toUpperCase(); // on server we return everything, labels, pageProps etc.

    if (!ctx.res?.headersSent && !(appProps.pageProps.initialAppState as AppState | undefined)) {
      throw new Error(
        `initialAppState is missing in ${pathname}. Make sure that you're returning it in getInitialProps.`,
      );
    }

    return {
      ...appProps,
      deviceType,
    };
  } else {
    const appProps = (await App.getInitialProps(appContext)) as Props;
    // on client we return { pageProps, initialData, deviceType } object
    // initialData is needed to avoid bugs when people come to the website
    // however, an individual page's props should not be passed
    // down since it overwrites the next page.
    // subpages, like the shelf page, etc., directly from a URL

    return {
      ...appProps,
      ...initialData,
    };
  }
};

export default CustomApp;
